import { useState } from "react";
import { useFetch, apiPost, fmtErr } from "@/lib/shieldx";
import { PageHeader, Panel, Loader, Stat } from "@/components/shieldx/Primitives";
import { DemoTag, StatusBadge, RiskBadge } from "@/components/shieldx/Badges";
import { Table } from "@/components/shieldx/Table";
import { ShieldHalf, Cpu, Play } from "lucide-react";
import { toast } from "sonner";

const DEC_TONE = {
  ALLOW: "text-emerald-400", CHALLENGE: "text-cyan-300", RATE_LIMIT: "text-amber-300",
  DENY: "text-rose-300", BLOCK: "text-red-400", ISOLATE: "text-orange-400",
};

export default function DefenseWall() {
  const { data, loading } = useFetch("/defense-wall/overview");
  const [form, setForm] = useState({ authorization: "VALID", in_scope: true, rpm: 120, risk: "LOW", profile: "NORMAL", classification: "AUTHORIZED AI SYSTEM", headless: false });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  if (loading || !data) return <Loader label="Loading Defense Wall" />;
  const m = data.metrics;

  const evaluate = async () => {
    setBusy(true);
    try { setResult(await apiPost("/defense-wall/evaluate", form)); }
    catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <PageHeader icon={ShieldHalf} title="Autonomous AI Defense Wall"
        subtitle="Fast Automation. Controlled Authority. Deterministic detect → evaluate → rate-limit → challenge → block → isolate, independent of AI."
        actions={<div className="flex items-center gap-2"><StatusBadge value={data.status === "ACTIVE" ? "ACTIVE" : "CONFIGURATION_REQUIRED"} /><DemoTag /></div>} />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <Stat label="Requests / sec" value={m.requests_per_sec} tone="blue" />
        <Stat label="Automated Requests" value={m.automated_requests.toLocaleString()} tone="cyan" />
        <Stat label="AI-Associated" value={m.ai_associated} tone="purple" />
        <Stat label="Authorized Sources" value={m.authorized_sources} tone="emerald" />
        <Stat label="Rate Limited" value={m.rate_limited} tone="amber" />
        <Stat label="Challenged" value={m.challenged} tone="cyan" />
        <Stat label="Blocked" value={m.blocked} tone="red" />
        <Stat label="Isolated" value={m.isolated} tone="amber" />
        <Stat label="Active Incidents" value={m.active_incidents} tone="red" />
        <Stat label="Protected Assets" value={m.protected_assets} tone="emerald" />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Panel title="Deterministic Decision Engine" desc="AI-independent — capability ≠ authority" className="lg:col-span-2" testid="dw-engine">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Authorization</label>
              <select data-testid="dw-authorization" value={form.authorization} onChange={(e) => set("authorization", e.target.value)} className="mt-1.5 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-200">
                {["VALID", "MISSING", "EXPIRED", "REVOKED"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Rate Profile</label>
              <select data-testid="dw-profile" value={form.profile} onChange={(e) => set("profile", e.target.value)} className="mt-1.5 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-200">
                {Object.keys(data.profiles).map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Requests / min</label>
              <input data-testid="dw-rpm" type="number" value={form.rpm} onChange={(e) => set("rpm", +e.target.value)} className="mt-1.5 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-200" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Behavioral Risk</label>
              <select data-testid="dw-risk" value={form.risk} onChange={(e) => set("risk", e.target.value)} className="mt-1.5 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-200">
                {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Source Classification</label>
              <select data-testid="dw-classification" value={form.classification} onChange={(e) => set("classification", e.target.value)} className="mt-1.5 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-200">
                {["AUTHORIZED AI SYSTEM", "AUTOMATED", "SUSPICIOUS AUTOMATION", "LIKELY HEADLESS", "UNKNOWN"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 pt-6 text-xs text-slate-300">
              <input type="checkbox" data-testid="dw-inscope" checked={form.in_scope} onChange={(e) => set("in_scope", e.target.checked)} className="h-4 w-4 accent-blue-600" /> In approved scope
            </label>
          </div>
          <button onClick={evaluate} disabled={busy} data-testid="dw-evaluate-btn" className="mt-4 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-40">
            <Play className="h-4 w-4" /> Evaluate Request
          </button>
          {result && (
            <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/50 p-4" data-testid="dw-result">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Deterministic Decision</span>
                <span className={`font-display text-xl font-bold ${DEC_TONE[result.decision] || "text-slate-200"}`}>{result.decision.replace("_", " ")}</span>
              </div>
              <p className="mt-1.5 text-sm text-slate-300">{result.reason}</p>
              <p className="mt-2 font-mono text-[10px] text-slate-600">{result.trace.join("  ·  ")}  ·  ai_independent=true</p>
            </div>
          )}
        </Panel>

        <Panel title="Rate-Limit Profiles" desc="Deterministic, per-source" testid="dw-profiles">
          <div className="space-y-2.5">
            {Object.entries(data.profiles).map(([name, p]) => (
              <div key={name} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                <p className="font-mono text-xs font-semibold text-slate-200">{name}</p>
                <p className="mt-1 font-mono text-[11px] text-slate-500">{p.rpm}/min · burst {p.burst} · concurrency {p.concurrency}</p>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="Live Defense Wall Events" desc="Observe → classify → authorize → policy → decide → audit" className="mt-4" testid="dw-stream">
        <Table testid="dw-stream-table" empty="No events"
          columns={[
            { key: "ts", label: "Time", render: (r) => <span className="font-mono text-[11px] text-slate-500">{new Date(r.ts).toISOString().substr(11, 8)}</span> },
            { key: "source_type", label: "Classification", render: (r) => <span className="text-slate-200">{r.source_type}</span> },
            { key: "asn", label: "ASN", render: (r) => <span className="font-mono text-[11px] text-slate-400">{r.asn}</span> },
            { key: "asset", label: "Asset", render: (r) => <span className="text-slate-300">{r.asset}</span> },
            { key: "authorization", label: "Authz", render: (r) => <span className="font-mono text-[11px] text-slate-400">{r.authorization}</span> },
            { key: "risk", label: "Risk", render: (r) => <RiskBadge value={r.risk} /> },
            { key: "decision", label: "Decision", render: (r) => <span className={`font-mono text-[11px] font-semibold ${DEC_TONE[r.decision] || "text-slate-300"}`}>{(r.decision || "").replace("_", " ")}</span> },
            { key: "incident", label: "Incident", render: (r) => <span className="font-mono text-[10px] text-slate-500">{r.incident || "—"}</span> },
          ]}
          rows={data.stream} />
      </Panel>
    </div>
  );
}
